import { CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/ui';
import type { CartItem as OrderItem } from '~/pages/checkout/types';
import { calculateOrderTotals, formatOrderTotal } from '~/utils';
import type { MsrInputsIssue } from './msrPurchaseOrderInputs';

type OrderSummaryProps = {
  isPlacingOrder?: boolean;
  onPlaceOrder?: () => void;
  items: OrderItem[];
  supplierName: string;
  isPlaceOrderDisabled?: boolean;
  isInternationalIncomplete?: boolean;
  isPurchaseOrderDatesIncomplete?: boolean;
  msrPurchaseOrderInputsIssue?: MsrInputsIssue;
  currency?: string | null;
  incoTerm?: string | null;
  productsNeedingCapacity: string[];
};

export function OrderSummary({
  isPlacingOrder,
  onPlaceOrder,
  items,
  supplierName,
  isPlaceOrderDisabled,
  isInternationalIncomplete,
  isPurchaseOrderDatesIncomplete,
  msrPurchaseOrderInputsIssue,
  currency: cartCurrency,
  incoTerm,
  productsNeedingCapacity,
}: OrderSummaryProps) {
  const itemsCount = items.length;
  const organizationName = supplierName;
  const { listedTotal, suggestedTotal } = calculateOrderTotals(
    { items },
    incoTerm
  );
  const currency = cartCurrency ?? undefined;
  const total = listedTotal;
  const suggestedTotalDisplay = suggestedTotal;
  const hasSuggestion = items.some(
    (item) => item.suggestedPrice != null && item.suggestedPrice.amount > 0
  );

  const { t } = useTranslation('cart');
  return (
    <div className="bg-neut-bg-alt rounded-lg border border-neut-border p-6 sticky top-6 shadow-neut-100">
      <h2 className="text-neut-prim mb-4 font-semibold">
        {t('cart.order_summary')}
      </h2>

      <div className="p-4 bg-prim-50 rounded-lg mb-4">
        <p className="text-xs text-neut-sec mb-1">{t('cart.supplier')}</p>
        <p className="text-sm text-neut-prim font-medium">{organizationName}</p>
        <p className="text-xs text-neut-sec"></p>
      </div>

      <div className="space-y-3 mb-6">
        <div className="flex items-center justify-between text-sm">
          <span className="text-neut-sec font-normal">
            {t('cart.subtotal', { itemsCount: itemsCount })}
          </span>
          <span className="text-neut-prim font-medium">
            {formatOrderTotal(listedTotal, t('cart.not_available'), currency)}
          </span>
        </div>
        <div className="border-t border-neut-border pt-3 flex items-center justify-between">
          <span className="text-neut-prim font-semibold">
            {t('cart.total')}
          </span>
          <span className="text-xl text-neut-prim font-semibold">
            {formatOrderTotal(total, t('cart.not_available'), currency)}
          </span>
        </div>
        {hasSuggestion && (
          <div className="flex items-center justify-between">
            <span className="text-neut-prim font-semibold">
              {t('cart.suggested_total')}
            </span>
            <span className="text-xl text-warning-700 font-semibold">
              {formatOrderTotal(
                suggestedTotalDisplay,
                t('cart.not_available'),
                currency
              )}
            </span>
          </div>
        )}
      </div>

      <Button
        onClick={onPlaceOrder}
        disabled={isPlaceOrderDisabled || isPlacingOrder}
      >
        <div className="flex gap-3 items-center justify-center">
          <CreditCard size={18} />
          {isPlacingOrder ? t('cart.placing_order') : t('cart.place_order')}
        </div>
      </Button>

      {productsNeedingCapacity.length > 0 && (
        <p className="text-xs text-warning-600 text-center mt-2">
          {t('cart.checkout_vehicle_capacity_missing', {
            products: productsNeedingCapacity.join(', '),
          })}
        </p>
      )}
      {isInternationalIncomplete && (
        <p className="text-xs text-warning-600 text-center mt-2">
          {t('cart.international.required_to_checkout')}
        </p>
      )}
      {isPurchaseOrderDatesIncomplete && (
        <p className="text-xs text-warning-600 text-center mt-2">
          {t('cart.purchase_order_dates.required_to_checkout')}
        </p>
      )}
      {msrPurchaseOrderInputsIssue != null && (
        <p className="text-xs text-warning-600 text-center mt-2">
          {t(
            msrPurchaseOrderInputsIssue === 'buyer_required'
              ? 'cart.msr_purchase_order_inputs.buyer_required_to_checkout'
              : 'cart.msr_purchase_order_inputs.required_to_checkout'
          )}
        </p>
      )}

      <p className="text-xs text-neut-500 text-center mt-3">
        {t('cart.order_summary_note')}
      </p>
    </div>
  );
}
